import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AlertOctagon, Plus, Trash2, Upload, Search, ShieldAlert } from "lucide-react";

const TYP_OPTIONS = [
    { value: "verramschung", label: "Verramschung" },
    { value: "makulatur", label: "Makulatur" },
    { value: "sonstiges", label: "Sonstiges" },
];

const TYP_COLOR = {
    verramschung: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    makulatur: "bg-red-500/15 text-red-400 border-red-500/30",
    sonstiges: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function SonderWb() {
    const { activeTenant } = useTenant();
    const { user } = useAuth();
    const { zeitraum } = usePeriod();
    const isAdmin = user?.role === "admin";

    const aktivesJahr = useMemo(() => {
        const n = parseInt(String(zeitraum).slice(0, 4), 10);
        return Number.isFinite(n) ? n : new Date().getFullYear();
    }, [zeitraum]);

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [filterJahr, setFilterJahr] = useState("");
    const [filterTyp, setFilterTyp] = useState("");
    const [hideErledigt, setHideErledigt] = useState(true);
    const [zeigeZukunft, setZeigeZukunft] = useState(false);

    // Add dialog
    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        isbn: "",
        typ: "verramschung",
        jahr: new Date().getFullYear(),
        notiz: "",
    });
    const [adding, setAdding] = useState(false);

    // Delete confirm
    const [delTarget, setDelTarget] = useState(null);

    // Import dialog
    const [importOpen, setImportOpen] = useState(false);
    const [importForm, setImportForm] = useState({
        jahr: new Date().getFullYear(),
        typ: "verramschung",
        isbn_spalte: "ISBN",
        file: null,
    });
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const load = async () => {
        if (!activeTenant) return;
        setLoading(true);
        try {
            const params = {
                tenant_id: activeTenant.tenant_id,
                typ: filterTyp || undefined,
            };
            if (filterJahr) {
                // Expliziter Jahr-Filter hat Vorrang
                params.jahr = filterJahr;
            } else if (!zeigeZukunft) {
                // Default: nur Einträge, die für die aktive Periode greifen
                params.jahr_max = aktivesJahr;
            }
            const r = await api.get("/sonder-wb", { params });
            setEvents(r.data);
        } catch {
            toast.error("Sonder-WB-Liste konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id, filterJahr, filterTyp, zeigeZukunft, aktivesJahr]);

    const filtered = useMemo(() => {
        let list = events;
        if (hideErledigt) list = list.filter((e) => !e.erledigt);
        if (search.trim()) {
            const q = search.toLowerCase().trim();
            list = list.filter(
                (e) =>
                    (e.isbn || "").toLowerCase().includes(q) ||
                    (e.haupttitel || "").toLowerCase().includes(q),
            );
        }
        return list;
    }, [events, hideErledigt, search]);

    const stats = useMemo(() => {
        const aktive = events.filter((e) => !e.erledigt).length;
        const erledigt = events.length - aktive;
        return { gesamt: events.length, aktive, erledigt };
    }, [events]);

    const addSubmit = async () => {
        if (!addForm.isbn.trim()) {
            toast.error("ISBN ist Pflicht");
            return;
        }
        setAdding(true);
        try {
            await api.post("/sonder-wb", {
                tenant_id: activeTenant.tenant_id,
                isbn: addForm.isbn.trim(),
                typ: addForm.typ,
                jahr: parseInt(addForm.jahr, 10),
                notiz: addForm.notiz || "",
            });
            toast.success(`Sonder-WB für ${addForm.isbn} angelegt`);
            setAddOpen(false);
            setAddForm({ isbn: "", typ: "verramschung", jahr: new Date().getFullYear(), notiz: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
        } finally {
            setAdding(false);
        }
    };

    const confirmDelete = async () => {
        if (!delTarget) return;
        try {
            await api.delete(`/sonder-wb/${delTarget.id}`, {
                params: { tenant_id: activeTenant.tenant_id },
            });
            toast.success("Eintrag gelöscht");
            setDelTarget(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    const importSubmit = async () => {
        if (!importForm.file) {
            toast.error("Bitte Datei auswählen");
            return;
        }
        setImporting(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append("tenant_id", activeTenant.tenant_id);
            fd.append("jahr", String(importForm.jahr));
            fd.append("typ", importForm.typ);
            fd.append("isbn_spalte", importForm.isbn_spalte || "ISBN");
            fd.append("file", importForm.file);
            const r = await api.post("/sonder-wb/import-excel", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportResult(r.data);
            toast.success(
                `Import abgeschlossen: ${r.data.angelegt} neu, ${r.data.duplikate} Duplikate, ${r.data.uebersprungen} übersprungen`,
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import fehlgeschlagen");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Daten</div>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1 flex items-center gap-3">
                    <AlertOctagon className="h-8 w-8 text-red-400" />
                    Sonder-Wertberichtigung
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                    Titel mit Sonder-WB (Verramschung, Makulatur etc.) werden{" "}
                    <strong className="text-foreground">unabhängig vom Alter</strong> zu{" "}
                    <strong className="text-red-400">100 % wertberichtigt</strong> — der Bilanzwert dieser Titel ist 0 €.
                    Sobald das Lager leer ist, wird der Eintrag als „erledigt" markiert.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-tenant/40 bg-tenant-soft text-tenant" data-testid="swb-active-period">
                    <span className="uppercase tracking-widest text-[10px]">Aktive Periode</span>
                    <strong>{aktivesJahr}</strong>
                    <span className="text-muted-foreground">— angezeigt werden Einträge mit „Gültig ab" ≤ {aktivesJahr}</span>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
                <Card className="p-4 bg-surface1 border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gültig für {aktivesJahr}</div>
                    <div className="font-heading text-2xl mt-1 tabular">{stats.gesamt}</div>
                </Card>
                <Card className="p-4 bg-surface1 border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">davon mit Bestand</div>
                    <div className="font-heading text-2xl mt-1 tabular text-red-400">{stats.aktive}</div>
                </Card>
                <Card className="p-4 bg-surface1 border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Erledigt (Lager = 0)</div>
                    <div className="font-heading text-2xl mt-1 tabular text-muted-foreground">{stats.erledigt}</div>
                </Card>
            </div>

            {/* Toolbar */}
            <Card className="p-4 bg-surface1 border-border">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="text-xs">Suche (ISBN, Titel)</Label>
                        <div className="relative mt-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="978-…"
                                className="h-9 pl-9"
                                data-testid="swb-search"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Genaues Jahr (überschreibt Default)</Label>
                        <Input
                            type="number"
                            value={filterJahr}
                            onChange={(e) => setFilterJahr(e.target.value)}
                            placeholder="Auto"
                            className="mt-1 h-9 w-[120px] tabular"
                            data-testid="swb-filter-jahr"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Typ</Label>
                        <select
                            value={filterTyp}
                            onChange={(e) => setFilterTyp(e.target.value)}
                            className="mt-1 h-9 w-[160px] px-2 rounded-md border border-border bg-surface2 text-xs"
                            data-testid="swb-filter-typ"
                        >
                            <option value="">Alle</option>
                            {TYP_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                        <Switch
                            checked={hideErledigt}
                            onCheckedChange={setHideErledigt}
                            id="swb-hide-erledigt"
                            data-testid="swb-hide-erledigt"
                        />
                        <Label htmlFor="swb-hide-erledigt" className="text-xs cursor-pointer">
                            Erledigte ausblenden
                        </Label>
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                        <Switch
                            checked={zeigeZukunft}
                            onCheckedChange={setZeigeZukunft}
                            id="swb-zeige-zukunft"
                            data-testid="swb-zeige-zukunft"
                        />
                        <Label htmlFor="swb-zeige-zukunft" className="text-xs cursor-pointer">
                            Auch zukünftige Einträge anzeigen
                        </Label>
                    </div>
                    <div className="flex gap-2 ml-auto">
                        {isAdmin && (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => { setImportOpen(true); setImportResult(null); }}
                                    data-testid="swb-import-btn"
                                >
                                    <Upload className="h-4 w-4 mr-2" /> Excel-Import
                                </Button>
                                <Button
                                    onClick={() => setAddOpen(true)}
                                    className="bg-red-500 hover:bg-red-600 text-white"
                                    data-testid="swb-add-btn"
                                >
                                    <Plus className="h-4 w-4 mr-2" /> Eintrag hinzufügen
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                    {filtered.length} Einträge angezeigt {hideErledigt && stats.erledigt > 0 && `(${stats.erledigt} erledigte ausgeblendet)`}
                </div>
            </Card>

            {/* Tabelle */}
            <Card className="bg-surface1 border-border overflow-hidden">
                <div className="overflow-auto max-h-[60vh]">
                    <Table>
                        <TableHeader className="sticky top-0 bg-surface1 z-10">
                            <TableRow>
                                <TableHead className="w-[140px]">ISBN</TableHead>
                                <TableHead>Haupttitel</TableHead>
                                <TableHead className="w-[140px]">Typ</TableHead>
                                <TableHead className="w-[100px] text-right">Gültig ab</TableHead>
                                <TableHead className="w-[120px] text-right">Bestand</TableHead>
                                <TableHead className="w-[120px]">Status</TableHead>
                                <TableHead className="w-[140px]">Erstellt</TableHead>
                                {isAdmin && <TableHead className="w-[60px]" />}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-muted-foreground py-8">
                                        Lade …
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-muted-foreground py-8">
                                        Keine Sonder-WB-Einträge.
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((e) => (
                                <TableRow key={e.id} className={e.erledigt ? "opacity-60" : ""} data-testid={`swb-row-${e.id}`}>
                                    <TableCell className="font-mono text-xs">{e.isbn}</TableCell>
                                    <TableCell className="max-w-[300px] truncate" title={e.haupttitel}>
                                        {e.haupttitel || <span className="text-muted-foreground italic">— Titel nicht gefunden</span>}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${TYP_COLOR[e.typ] || ""}`}>
                                            {e.typ}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right tabular">{e.jahr}</TableCell>
                                    <TableCell className="text-right tabular">{e.aktueller_bestand}</TableCell>
                                    <TableCell>
                                        {e.erledigt ? (
                                            <Badge variant="outline" className="text-[10px] bg-muted/30 text-muted-foreground">
                                                Erledigt
                                            </Badge>
                                        ) : (
                                            <Badge className="text-[10px] bg-red-500/20 text-red-300 border-red-500/30">
                                                Aktiv · 100 % WB
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {e.erstellt_am ? new Date(e.erstellt_am).toLocaleDateString("de-DE") : "—"}
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setDelTarget(e)}
                                                className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400"
                                                data-testid={`swb-delete-${e.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            {/* Add-Dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <AlertOctagon className="h-5 w-5 text-red-400" />
                            Sonder-WB hinzufügen
                        </DialogTitle>
                        <DialogDescription>
                            Der Titel wird ab dem angegebenen Jahr und allen folgenden Bewertungsperioden zu 100 % wertberichtigt.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">ISBN *</Label>
                            <Input
                                value={addForm.isbn}
                                onChange={(e) => setAddForm({ ...addForm, isbn: e.target.value })}
                                placeholder="978-…"
                                className="mt-1 font-mono"
                                data-testid="swb-add-isbn"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Typ</Label>
                                <select
                                    value={addForm.typ}
                                    onChange={(e) => setAddForm({ ...addForm, typ: e.target.value })}
                                    className="mt-1 h-10 w-full px-3 rounded-md border border-border bg-surface2 text-sm"
                                    data-testid="swb-add-typ"
                                >
                                    {TYP_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs">Gültig ab Jahr *</Label>
                                <Input
                                    type="number"
                                    value={addForm.jahr}
                                    onChange={(e) => setAddForm({ ...addForm, jahr: e.target.value })}
                                    className="mt-1 tabular"
                                    data-testid="swb-add-jahr"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Notiz (optional)</Label>
                            <Input
                                value={addForm.notiz}
                                onChange={(e) => setAddForm({ ...addForm, notiz: e.target.value })}
                                placeholder="z.B. Restbestand-Aktion 03/2025"
                                className="mt-1"
                                data-testid="swb-add-notiz"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={adding}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={addSubmit}
                            disabled={adding || !addForm.isbn.trim()}
                            className="bg-red-500 hover:bg-red-600 text-white"
                            data-testid="swb-add-submit"
                        >
                            {adding ? "Speichere …" : "Anlegen"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import-Dialog */}
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <Upload className="h-5 w-5" />
                            Sonder-WB Excel-Import
                        </DialogTitle>
                        <DialogDescription>
                            Datei muss eine Spalte mit ISBNs enthalten. Alle anderen Spalten werden ignoriert.
                            Duplikate (gleiche ISBN + Jahr + Typ) werden übersprungen.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Gültig ab Jahr *</Label>
                                <Input
                                    type="number"
                                    value={importForm.jahr}
                                    onChange={(e) => setImportForm({ ...importForm, jahr: e.target.value })}
                                    className="mt-1 tabular"
                                    data-testid="swb-import-jahr"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Typ</Label>
                                <select
                                    value={importForm.typ}
                                    onChange={(e) => setImportForm({ ...importForm, typ: e.target.value })}
                                    className="mt-1 h-10 w-full px-3 rounded-md border border-border bg-surface2 text-sm"
                                    data-testid="swb-import-typ"
                                >
                                    {TYP_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Spaltenname der ISBN-Spalte</Label>
                            <Input
                                value={importForm.isbn_spalte}
                                onChange={(e) => setImportForm({ ...importForm, isbn_spalte: e.target.value })}
                                placeholder="ISBN"
                                className="mt-1"
                                data-testid="swb-import-isbn-col"
                            />
                            <div className="text-[10px] text-muted-foreground mt-1">
                                Default „ISBN". Anpassen, wenn die Spalte in Ihrer Datei z.B. „EAN" oder „Artikelnummer" heißt.
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Excel-Datei (.xlsx) *</Label>
                            <Input
                                type="file"
                                accept=".xlsx"
                                onChange={(e) => setImportForm({ ...importForm, file: e.target.files?.[0] || null })}
                                className="mt-1"
                                data-testid="swb-import-file"
                            />
                        </div>
                        {importResult && (
                            <div className="rounded-md border border-border p-3 text-xs space-y-1 bg-surface2">
                                <div><strong className="text-emerald-400">{importResult.angelegt}</strong> neu angelegt</div>
                                <div><strong className="text-yellow-400">{importResult.duplikate}</strong> Duplikate übersprungen</div>
                                <div><strong className="text-red-400">{importResult.uebersprungen}</strong> ungültig (z.B. fehlende ISBN)</div>
                                {importResult.fehler?.length > 0 && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-muted-foreground">Fehler-Details</summary>
                                        <ul className="mt-1 ml-3 list-disc text-muted-foreground">
                                            {importResult.fehler.map((f, i) => <li key={i}>{f}</li>)}
                                        </ul>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setImportOpen(false)} disabled={importing}>
                            Schließen
                        </Button>
                        <Button
                            onClick={importSubmit}
                            disabled={importing || !importForm.file}
                            className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                            data-testid="swb-import-submit"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            {importing ? "Importiere …" : "Importieren"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete-Confirm */}
            <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-red-400" />
                            Eintrag löschen?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            ISBN <strong>{delTarget?.isbn}</strong> ({delTarget?.typ}, {delTarget?.jahr})
                            wird gelöscht. Die Bewertung dieses Titels wird wieder über die normale Staffel berechnet.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Endgültig löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
